import {
  AgxButton,
  AgxFileUpload,
  AgxLabel,
  AgxMultiSelect,
  EntityImage,
  AgxColours,
} from '@urbanx/agx-ui-components';
import { AgxFormTextField, type FormSubmissionResult } from '@urbanx/agx.forms';
import { StaffRole } from '@urbanx/agx.auth.client';
import { States, UploadedDocument } from '@urbanx/agx.staff.client';
import { AgxSpacer } from '@urbanx/agx.ui-components2';
import { useEffect, useImperativeHandle } from 'react';

import { StaffAction } from 'types/staff';
import { NotificationState } from 'types/commonTypes';
import { StaffMemberForm, type StaffMemberFormFields } from './staffMemberFormDefinition';

const selectionsMatch = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value) => right.includes(value));

export interface StaffMemberFormValues {
  id?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
  title?: string | undefined;
  email?: string | undefined;
  phoneNumber?: string | undefined;
  emailInvitationSent?: boolean | undefined;
  photo?: UploadedDocument | undefined;
  hasLoggedIn?: boolean | undefined;
  roles?: string[] | undefined;
  states?: States[] | undefined;
}

export interface StaffMemberFormHandle {
  submit(): Promise<void>;
}

export type StaffMemberSubmissionResult = FormSubmissionResult<StaffMemberFormFields>;

interface StaffMemberFormProps {
  ref?: React.Ref<StaffMemberFormHandle>;
  pageAction: StaffAction.CREATE | StaffAction.EDIT;
  staffMember: StaffMemberFormValues;
  photoUri?: string;
  isSaving: boolean;
  isPhotoActionPending: boolean;
  availableStaffRoleOptions: Array<{ label: string; value: string | StaffRole }>;
  stateOptions: Array<{ value: States; label: string }>;
  showAgencies: boolean;
  agencyIds: string[];
  agenciesOptions: Array<{ label?: string; value?: string }>;
  showEmailInvitation: boolean;
  emailInviteState: NotificationState;
  emailInviteDisabled: boolean;
  onValidSubmit: (
    updatedStaffMember: StaffMemberFormValues
  ) => Promise<StaffMemberSubmissionResult>;
  onDirtyChange: (isDirty: boolean) => void;
  onRolesChange: (roles: string[]) => void;
  onAgencyIdsChange: (value: string[]) => void;
  onUploadPhoto: (file: File) => Promise<unknown>;
  onRemovePhoto: () => void;
  onSendInvite?: () => void;
}

const BoundStaffMemberFormPage = ({
  ref,
  pageAction,
  staffMember,
  photoUri,
  isSaving,
  isPhotoActionPending,
  availableStaffRoleOptions,
  stateOptions,
  showAgencies,
  agencyIds,
  agenciesOptions,
  showEmailInvitation,
  emailInviteState,
  emailInviteDisabled,
  onValidSubmit,
  onDirtyChange,
  onRolesChange,
  onAgencyIdsChange,
  onUploadPhoto,
  onRemovePhoto,
  onSendInvite,
}: StaffMemberFormProps) => {
  const form = StaffMemberForm.useForm({
    defaultValues: {
      firstName: staffMember.firstName ?? '',
      lastName: staffMember.lastName ?? '',
      title: staffMember.title ?? '',
      email: staffMember.email ?? '',
      phoneNumber: staffMember.phoneNumber ?? '',
      roles: staffMember.roles ?? [],
      states: staffMember.states?.map(String) ?? [],
    },
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) =>
      onValidSubmit({
        ...staffMember,
        ...parsedValues,
        states: parsedValues.states as States[],
      }),
  });
  useImperativeHandle(ref, () => ({ submit: form.submit }), [form.submit]);

  useEffect(() => onDirtyChange(form.isDirty), [form.isDirty, onDirtyChange]);

  return (
    <StaffMemberForm.Root form={form}>
      <AgxSpacer direction="vertical" gap="spacing-5xl" className="container50Percent">
        <AgxSpacer direction="horizontal" gap="spacing-xl" className="flexCentre" fill>
          <EntityImage
            addRingToImage
            backgroundColour={`var(--${AgxColours.NEUTRAL_GREY_500})`}
            photoUri={photoUri}
            size={72}
            usePlaceholderImage
          />
          <AgxSpacer direction="horizontal" gap="spacing-xl" contentAlignment="center">
            <AgxFileUpload
              id="fileUpload"
              title=""
              buttonText={photoUri ? 'Change' : 'Upload'}
              isImageFileType
              disabled={isSaving || isPhotoActionPending}
              uploadFile={onUploadPhoto}
              extensions={['jpg', 'jpeg', 'png', 'svg', 'bmp']}
            />
            {photoUri && (
              <AgxButton
                text="Remove"
                medium
                hollow
                primary
                disabled={isSaving || isPhotoActionPending}
                onClick={onRemovePhoto}
              />
            )}
          </AgxSpacer>
        </AgxSpacer>

        <AgxSpacer direction="horizontal" gap="spacing-5xl">
          <StaffMemberForm.Field
            name="firstName"
            Input={AgxFormTextField}
            type="text"
            label="First name"
            stretch
            hideOptionalText
          />
          <StaffMemberForm.Field
            name="lastName"
            Input={AgxFormTextField}
            type="text"
            label="Last name"
            stretch
            hideOptionalText
          />
        </AgxSpacer>

        <AgxSpacer direction="horizontal">
          <StaffMemberForm.Field
            name="title"
            Input={AgxFormTextField}
            type="text"
            label="Job Title"
            stretch
            hideOptionalText
          />
        </AgxSpacer>

        <AgxSpacer direction="horizontal">
          <StaffMemberForm.Field
            name="email"
            Input={AgxFormTextField}
            disabled={pageAction === StaffAction.EDIT}
            type="email"
            label="Email"
            stretch
            hideOptionalText
          />
        </AgxSpacer>

        <AgxSpacer direction="horizontal" gap="spacing-5xl">
          <StaffMemberForm.Field
            name="phoneNumber"
            Input={AgxFormTextField}
            type="phone"
            label="Phone"
            stretch
            maxLength={15}
          />
        </AgxSpacer>

        <StaffMemberForm.CustomField name="roles">
          {(field) => (
            <AgxSpacer direction="horizontal">
              <AgxMultiSelect
                id="SetRoles"
                label="Role(s)"
                options={availableStaffRoleOptions}
                defaultValue={field.value}
                hideOptionalLabel
                parentControlValue
                disabled={field.disabled}
                onValueChanged={({ value }: { value: string[] }) => {
                  if (selectionsMatch(value, field.value)) return;
                  field.onChange(value);
                  onRolesChange(value);
                }}
                required={field.required}
                error={field.errorText}
              />
            </AgxSpacer>
          )}
        </StaffMemberForm.CustomField>

        <StaffMemberForm.CustomField name="states">
          {(field) =>
            field.visible ? (
              <AgxSpacer direction="horizontal">
                <AgxMultiSelect
                  id="SetStates"
                  label="State(s)"
                  options={stateOptions}
                  defaultValue={field.value}
                  hideOptionalLabel
                  parentControlValue
                  disabled={field.disabled}
                  onValueChanged={({ value }: { value: string[] }) => {
                    if (selectionsMatch(value, field.value)) return;
                    field.onChange(value);
                  }}
                  required={field.required}
                  error={field.errorText}
                />
              </AgxSpacer>
            ) : null
          }
        </StaffMemberForm.CustomField>

        {showAgencies && (
          <AgxSpacer direction="horizontal">
            <AgxMultiSelect
              id="selAgenciesForStaff"
              label="Agencies"
              hideOptionalLabel
              defaultValue={agencyIds}
              options={agenciesOptions}
              disabled={isSaving || form.phase === 'submitting'}
              onValueChanged={({ value }: { value: string[] }) => onAgencyIdsChange(value)}
              parentControlValue
            />
          </AgxSpacer>
        )}

        {showEmailInvitation && (
          <AgxSpacer direction="horizontal" fill>
            <AgxSpacer direction="vertical" gap="spacing-md">
              <AgxLabel medium>Email Invitation</AgxLabel>
              <AgxButton
                text={
                  emailInviteState !== NotificationState.Sending
                    ? `${
                        staffMember.emailInvitationSent ||
                        emailInviteState === NotificationState.Sent
                          ? 'Resend'
                          : 'Send'
                      } Invite`
                    : 'Sending...'
                }
                medium
                primary
                disabled={emailInviteDisabled}
                extraClasses={
                  emailInviteState === NotificationState.Sending ? 'emailInviteButton' : ''
                }
                onClick={() => onSendInvite?.()}
              />
            </AgxSpacer>
          </AgxSpacer>
        )}
      </AgxSpacer>
    </StaffMemberForm.Root>
  );
};

const StaffMemberFormPage = (props: StaffMemberFormProps) => (
  <BoundStaffMemberFormPage key={props.staffMember.id ?? 'new-staff-member'} {...props} />
);

export default StaffMemberFormPage;
